import React, { useState, useEffect, useRef } from "react";
import { Table, Typography, Tag, Space } from "antd";
import { withTranslation } from "react-i18next";
import moment from "moment";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import {
  PRIMARY_50,
  PRIMARY_300,
  PRIMARY_600,
  PRIMARY_700,
  SUCCESS_50,
  SUCCESS_700,
  SUCCESS_300,
  WARNING_50,
  WARNING_600,
  ERROR_50,
  ERROR_600,
  GRAY_50,
  GRAY_300,
  GRAY_400,
  GRAY_600,
  BLACK
} from "../../../constant/colors";
import { useTranslation } from "react-i18next";
import { fontWeights, sizes } from "../../../constant/styles";
import { APPLICATIONS, getApplicationStatusText } from "../../../constant/applications";

import { PAGE_SIZE } from "../../../constant/table";
import { TailormedSpinner } from "../../TailormedSpinner/TailormedSpinner";

import { commonTableStyle } from "../TableStyle";
import EmptyTableMessage from "../EmptyTableMessage";
import PatientPageCount from "../PatientPageCount";

import { PatientNameColumn } from "../CommonColumns";
import { getSelfEnrollInvitationsAPI } from "../../../api/api";

const INVITE_SENT_STATUS = "invite sent";
const EMPTY_VALUE_DISPLAY = "–";
const EXPANSION_ICON_SIZE = 30;

// Column keys enum for switch cases [[memory:4820653]]
const COLUMN_KEYS = Object.freeze({
  EXPANDER: "expander",
  PATIENT: "patient",
  DATE_LAST_SENT: "dateLastSent",
  PATIENT_VIEWED: "patientViewed",
  APPLICATION_CREATED: "applicationCreated",
  DATE_CREATED: "dateCreated",
  STATUS: "status"
});

import "../cursor.less";

const { Text } = Typography;

// Styled table to match exactly with AllPatientsTable
const StyledTable = styled(Table)`
  ${(props) => commonTableStyle(props.isFilterListView)}
  .ant-table-thead > tr > th:nth-last-of-type(-n + 2)::before {
    display: none;
  }
  /* Collapse AntD default expand column completely */
  .ant-table-thead > tr > th.ant-table-row-expand-icon-cell {
    width: 0 !important;
    padding: 0 !important;
    border: none !important;
  }
  .ant-table-tbody > tr > td.ant-table-row-expand-icon-cell {
    width: 0 !important;
    padding: 0 !important;
    border: none !important;
  }
  /* First (expander) column sizing + slight left padding */
  .ant-table-thead > tr > th.expander-col {
    padding-left: 4px !important;
    padding-right: 0 !important;
    width: 28px;
    text-align: center;
  }
  .ant-table-tbody > tr > td.expander-col {
    padding-left: 4px !important;
    padding-right: 0 !important;
    width: 28px;
    text-align: center;
  }

  /* Style for expanded row container */
  .ant-table-expanded-row > .ant-table-cell {
    padding: 0 !important;
  }

  .ant-table-expanded-row-fixed {
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Maintain cursor pointer for parent rows */
  .ant-table-tbody > tr.cursor-pointer {
    cursor: pointer;
  }
`;

// Custom expansion icon component
const ExpansionIcon = ({ expanded, onClick }) => {
  const iconStyle = {
    cursor: "pointer",
    transition: "transform 0.3s ease",
    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
    transformOrigin: "center center",
    display: "inline-block",
    width: `${EXPANSION_ICON_SIZE}px`,
    height: `${EXPANSION_ICON_SIZE}px`
  };

  return (
    <div onClick={onClick} style={iconStyle}>
      <svg
        width={EXPANSION_ICON_SIZE}
        height={EXPANSION_ICON_SIZE}
        viewBox={`0 0 ${EXPANSION_ICON_SIZE} ${EXPANSION_ICON_SIZE}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          width={EXPANSION_ICON_SIZE}
          height={EXPANSION_ICON_SIZE}
          rx={EXPANSION_ICON_SIZE / 2}
          fill={PRIMARY_600}
        />
        <path
          d="M9.75 17.625L15 12.375L20.25 17.625"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

// Status color configuration using system constants [[memory:4820653]]
const STATUS_COLORS = Object.freeze({
  [APPLICATIONS.statuses.prepare]: {
    bg: SUCCESS_50,
    text: SUCCESS_700,
    border: SUCCESS_300
  },
  [APPLICATIONS.statuses.applied]: {
    bg: SUCCESS_50,
    text: SUCCESS_700,
    border: SUCCESS_300
  },
  [APPLICATIONS.statuses.appliedToWaitlist]: {
    bg: WARNING_50,
    text: WARNING_600,
    border: WARNING_600
  },
  [APPLICATIONS.statuses.approved]: {
    bg: SUCCESS_50,
    text: SUCCESS_700,
    border: SUCCESS_300
  },
  [APPLICATIONS.statuses.rejected]: {
    bg: ERROR_50,
    text: ERROR_600,
    border: ERROR_600
  },
  [APPLICATIONS.statuses.ineligible]: {
    bg: ERROR_50,
    text: ERROR_600,
    border: ERROR_600
  }
});

// Enhanced status colors for better differentiation
const getApplicationStatusTag = (status, t) => {
  const config = STATUS_COLORS[status] || {
    bg: GRAY_50,
    text: GRAY_600,
    border: GRAY_300
  };

  return (
    <Tag
      style={{
        backgroundColor: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        borderRadius: "100px",
        padding: "2px 8px",
        fontSize: sizes.small,
        fontWeight: fontWeights.semibold,
        textTransform: "uppercase"
      }}
    >
      {status ? getApplicationStatusText(status, t, EMPTY_VALUE_DISPLAY) : EMPTY_VALUE_DISPLAY}
    </Tag>
  );
};

const PatientEnrollSentTableComponent = ({ isFilterListView }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [expandedRowWidths, setExpandedRowWidths] = useState({});
  const tableRef = useRef(null);

  // State for self-enrollment invitations data
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0
  });

  // Define displayData early
  const displayData = invitations;

  // Function to measure column widths for a specific row
  const measureRowWidths = (rowKey) => {
    const parentRow = tableRef.current?.querySelector(`[data-row-key="${rowKey}"]`);
    const widths = [];

    if (parentRow) {
      const cells = parentRow.querySelectorAll(".ant-table-cell");
      cells.forEach((cell) => {
        widths.push(cell.offsetWidth);
      });
    }

    setExpandedRowWidths((prev) => ({
      ...prev,
      [rowKey]: widths
    }));
  };

  // Force re-render of expanded rows when layout changes
  React.useEffect(() => {
    const handleResize = () => {
      // Re-measure all expanded rows
      expandedRowKeys.forEach((rowKey) => {
        measureRowWidths(rowKey);
      });
      setForceUpdate((prev) => prev + 1);
    };

    // Set up ResizeObserver to detect table layout changes
    let resizeObserver;
    if (tableRef.current) {
      resizeObserver = new ResizeObserver(handleResize);
      const tableWrapper = tableRef.current.querySelector(".ant-table-wrapper");
      if (tableWrapper) {
        resizeObserver.observe(tableWrapper);
      }
    }

    // Also listen for window resize
    window.addEventListener("resize", handleResize);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [expandedRowKeys]);

  // Measure widths when rows are expanded
  React.useEffect(() => {
    expandedRowKeys.forEach((rowKey) => {
      measureRowWidths(rowKey);
    });
  }, [expandedRowKeys, forceUpdate]);

  const getStatusTag = (inviteRecord) => {
    // If no successful delivery (sentAt is null), this shouldn't appear in the table anymore
    // But keeping for safety
    if (!inviteRecord.sentAt) {
      return (
        <Tag
          style={{
            backgroundColor: ERROR_50,
            color: ERROR_600,
            border: `1px solid ${ERROR_600}`,
            borderRadius: "100px",
            padding: "2px 8px",
            fontSize: sizes.small,
            fontWeight: fontWeights.semibold,
            textTransform: "uppercase"
          }}
        >
          {t("selfEnrollment.failed")}
        </Tag>
      );
    }
    if (inviteRecord.status === null) {
      // Multiple applications - show nothing
      return EMPTY_VALUE_DISPLAY;
    }

    if (inviteRecord.status === INVITE_SENT_STATUS) {
      return (
        <Tag
          style={{
            backgroundColor: PRIMARY_50,
            color: PRIMARY_700,
            border: `1px solid ${PRIMARY_300}`,
            borderRadius: "100px",
            padding: "2px 8px",
            fontSize: sizes.small,
            fontWeight: fontWeights.semibold,
            textTransform: "uppercase"
          }}
        >
          {t("selfEnrollment.invite_sent")}
        </Tag>
      );
    }

    // Single application status with enhanced colors
    return getApplicationStatusTag(inviteRecord.status, t);
  };

  const getApplicationPrograms = (record) => {
    if (!record.applications || record.applications.length === 0) {
      return EMPTY_VALUE_DISPLAY;
    }

    if (record.applications.length === 1) {
      return record.applications[0].programName || t("selfEnrollment.unknown_program_fallback");
    } else {
      return t("selfEnrollment.multiple_programs");
    }
  };

  // Memoized columns to prevent re-creation on every render
  const columns = React.useMemo(
    () => [
      {
        title: "",
        key: COLUMN_KEYS.EXPANDER,
        width: 40,
        className: "expander-col",
        render: (_, record) => (
          <div style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {record.hasApplications && record.applications && record.applications.length > 1 ? (
              <ExpansionIcon
                expanded={expandedRowKeys.includes(record.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  const key = record.id;
                  if (expandedRowKeys.includes(key)) {
                    setExpandedRowKeys((prev) => prev.filter((k) => k !== key));
                  } else {
                    setExpandedRowKeys((prev) => [...prev, key]);
                  }
                }}
              />
            ) : (
              <div style={{ width: EXPANSION_ICON_SIZE, height: EXPANSION_ICON_SIZE }} />
            )}
          </div>
        )
      },
      {
        title: t("selfEnrollment.patient_name_and_id"),
        key: COLUMN_KEYS.PATIENT,
        width: 244,
        render: (_, record) => {
          return (
            <PatientNameColumn
              record={{
                name: record.patientName, // This is already the full name or null
                mrn: record.patientMrn,
                patientId: record.patientId
              }}
            />
          );
        }
      },
      {
        title: t("selfEnrollment.date_last_sent"),
        key: COLUMN_KEYS.DATE_LAST_SENT,
        width: 145,
        render: (_, record) => {
          const sentAt = record.sentAt;
          const userName =
            record.generatedByName || record.generatedBy || record.createdBy || t("selfEnrollment.unknown_user");
          const formattedDate = moment(sentAt).format("MM/DD/YYYY");
          return (
            <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
              <Text style={{ fontSize: sizes.medium, color: BLACK }}>{formattedDate}</Text>
              <Text style={{ fontSize: sizes.medium, color: BLACK }}>{userName}</Text>
            </Space>
          );
        }
      },
      {
        title: t("selfEnrollment.patient_viewed"),
        key: COLUMN_KEYS.PATIENT_VIEWED,
        width: 140,
        render: (_, record) => {
          const viewedAt = record.patientViewed;
          if (!viewedAt) {
            return <Text style={{ color: GRAY_400 }}>{EMPTY_VALUE_DISPLAY}</Text>;
          }
          return <Text>{moment(viewedAt).format("MM/DD/YYYY")}</Text>;
        }
      },
      {
        title: t("selfEnrollment.application_created_by_patient"),
        key: COLUMN_KEYS.APPLICATION_CREATED,
        width: 322,
        render: (_, record) => <Text>{getApplicationPrograms(record)}</Text>
      },
      {
        title: t("selfEnrollment.application_created"),
        key: COLUMN_KEYS.DATE_CREATED,
        width: 191,
        render: (_, record) => {
          // If there are no applications, show nothing
          if (!record.applications || record.applications.length === 0) {
            return <Text>{EMPTY_VALUE_DISPLAY}</Text>;
          }
          // For multiple applications, leave empty in parent row
          if (record.applications.length > 1) {
            return <Text>{EMPTY_VALUE_DISPLAY}</Text>;
          }
          // For single application, show the date
          return <Text>{moment(record.applications[0].createdAt).format("MM/DD/YYYY")}</Text>;
        }
      },
      {
        title: t("selfEnrollment.status"),
        key: COLUMN_KEYS.STATUS,
        width: 97,
        render: (_, record) => getStatusTag(record)
      }
    ],
    [t, expandedRowKeys]
  );

  // Removed state for invitation history since we're not showing it anymore

  // Fetch data from API
  const fetchInvitations = async (page = 1, filters = {}) => {
    setLoading(true);
    try {
      const response = await getSelfEnrollInvitationsAPI(filters, {
        page,
        limit: PAGE_SIZE
      });

      if (response && response.data && response.data.invitations) {
        // Filter out invitations where sentAt is null
        const filteredInvitations = response.data.invitations.filter((invitation) => invitation.sentAt !== null);

        setInvitations(filteredInvitations);
        setPagination((prev) => ({
          ...prev,
          current: page,
          total: response.data.pagination?.total || 0
        }));
      }
    } catch (error) {
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchInvitations();
  }, []);

  return (
    <div ref={tableRef}>
      <StyledTable
        size="small"
        style={{ width: "100%" }}
        isFilterListView={isFilterListView}
        loading={{
          indicator: <TailormedSpinner />,
          spinning: loading
        }}
        columns={columns}
        locale={{
          emptyText: <EmptyTableMessage />
        }}
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: pagination.current,
          defaultPageSize: PAGE_SIZE,
          pageSize: PAGE_SIZE,
          position: ["bottom"],
          total: pagination.total,
          showTotal: (total, range) => <PatientPageCount total={total} range={range} isAccurateCount={true} />,
          onChange: (page) => fetchInvitations(page)
        }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: setExpandedRowKeys,
          expandedRowRender: (record) => {
            // Only render if there are multiple applications
            if (!record.applications || record.applications.length <= 1) {
              return null;
            }

            // Get measured widths for this row
            const cellWidths = expandedRowWidths[record.key] || [];

            // Create rows that exactly match the parent table structure
            return (
              <div
                style={{
                  margin: 0,
                  padding: 0,
                  width: "100%"
                }}
              >
                {record.applications.map((app) => (
                  <div
                    key={app.id}
                    style={{
                      display: "flex",
                      backgroundColor: GRAY_50,
                      minHeight: "53px",
                      alignItems: "center"
                    }}
                  >
                    {columns.map((col, colIndex) => {
                      // Use measured width from parent row or fall back to column width
                      const width = cellWidths[colIndex] || col.width || 100;

                      return (
                        <div
                          key={col.key}
                          style={{
                            width: `${width}px`,
                            minWidth: `${width}px`,
                            maxWidth: `${width}px`,
                            padding: col.key === "expander" ? "5px 0px" : "5px 0px 5px 20px",
                            textAlign: col.key === "expander" ? "center" : "left",
                            boxSizing: "border-box",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {(() => {
                            switch (col.key) {
                              case COLUMN_KEYS.EXPANDER:
                              case COLUMN_KEYS.PATIENT:
                              case COLUMN_KEYS.DATE_LAST_SENT:
                              case COLUMN_KEYS.PATIENT_VIEWED:
                                return null; // Empty for first four columns
                              case COLUMN_KEYS.APPLICATION_CREATED:
                                return <Text>{app.programName || t("selfEnrollment.unknown_program_fallback")}</Text>;
                              case COLUMN_KEYS.DATE_CREATED:
                                return (
                                  <Text>
                                    {app.createdAt ? moment(app.createdAt).format("MM/DD/YYYY") : EMPTY_VALUE_DISPLAY}
                                  </Text>
                                );
                              case COLUMN_KEYS.STATUS:
                                return getApplicationStatusTag(app.status, t);
                              default:
                                return null;
                            }
                          })()}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          },
          expandRowByClick: false,
          rowExpandable: (record) => record.hasApplications && record.applications && record.applications.length > 1,
          expandIcon: () => null, // We're using custom expand buttons in the first column
          showExpandColumn: false,
          columnWidth: 0
        }}
        dataSource={
          displayData
            ? displayData.map((patient) => ({
                ...patient,
                key: patient.key || patient.id,
                expanded: expandedRowKeys.includes(patient.key || patient.id)
              }))
            : []
        }
        onRow={(record) => ({
          onClick: () => {
            const patientId = record.patientId || record.id;
            const journeyId = record.journeyId;
            const potentialSavingId = record.potentialSavingId || record.papId || record.optimizationId;
            // Navigate to Potential Savings page for this patient
            if (patientId && journeyId) {
              const basePath = `/patient/${patientId}/journey/${journeyId}/optimizations`;
              const navigationPath = potentialSavingId ? `${basePath}/${potentialSavingId}` : basePath;
              history.push(navigationPath);
            }
          }
        })}
        rowClassName="cursor-pointer"
      />
    </div>
  );
};

const PatientEnrollSentTable = withTranslation()(PatientEnrollSentTableComponent);

export { PatientEnrollSentTable };
